import * as swcHelpers from '@swc/helpers';
import { DataLayer, getIncludedVatAmount, calcPrice, priceVatCalc } from '@grandvisionhq/analytics';
import { Locales } from '@grandvisionhq/state';
import { getLocalizedAttrValue, getJoinedLocalizedAttrValues, getAttrValue } from '@grandvisionhq/utils';

const calcSumPrices = (...args)=>calcPrice(args.reduce((sum, n)=>sum + (n !== null && n !== void 0 ? n : 0)
    , 0))
;
const parsePrice = (price, vatRate)=>{
    var ref;
    if (!price) return {
        regularPrice: null,
        discountedPrice: null,
        discountAmount: null,
        vatAmount: null
    };
    const regularPrice = price.value.centAmount;
    var _centAmount;
    const discountedPrice = (_centAmount = (ref = price.discounted) === null || ref === void 0 ? void 0 : ref.value.centAmount) !== null && _centAmount !== void 0 ? _centAmount : null;
    const { vatAmount  } = discountedPrice !== null ? priceVatCalc(discountedPrice, vatRate) : priceVatCalc(regularPrice, vatRate);
    return {
        regularPrice,
        discountedPrice,
        discountAmount: discountedPrice ? regularPrice - discountedPrice : 0,
        vatAmount
    };
};
const getPrices = (variant, vat, offering)=>{
    if (!variant) {
        console.warn('No variant data found.');
        return null;
    }
    if (typeof vat === 'undefined') {
        console.warn(`No VAT amount found for variant with SKU ${variant.sku}.`);
        return null;
    }
    var _prices;
    const pricing = ((_prices = variant.prices) !== null && _prices !== void 0 ? _prices : []).find((price)=>typeof price.value.centAmount !== 'undefined'
    );
    if (!pricing) {
        console.warn(`Variant with SKU ${variant.sku} doesn't have any pricing.`);
        return null;
    }
    const productPricing = parsePrice(pricing, vat);
    const offeringPricing = parsePrice(offering === null || offering === void 0 ? void 0 : offering.price, vat);
    return {
        original: calcSumPrices(productPricing.regularPrice, offeringPricing.regularPrice),
        discountedPrice: productPricing.discountedPrice !== null ? calcSumPrices(productPricing.discountedPrice, offeringPricing.discountedPrice) : calcSumPrices(productPricing.regularPrice, offeringPricing.regularPrice),
        discountAmount: calcSumPrices(productPricing.discountAmount, offeringPricing.discountAmount),
        vatAmount: calcSumPrices(productPricing.vatAmount, offeringPricing.vatAmount)
    };
};
const getSharedAttributes = (product, purchaseType, activeVariant, lineItemType, pricePerMonth, offering)=>{
    const { sku  } = activeVariant;
    const includedVatAmount = getIncludedVatAmount(product);
    const isCLBBSubscription = !!(purchaseType === DataLayer.DataLayerTypes.PurchaseType.CLBBSubscription);
    const prices = !isCLBBSubscription ? getPrices(activeVariant, includedVatAmount, offering) : null;
    const clbbPrice = pricePerMonth ? calcPrice(pricePerMonth) : '';
    const name = DataLayer.DataLayerUtils.getNameFromVariant(activeVariant);
    return {
        name,
        id: sku || '',
        price: !isCLBBSubscription ? (prices === null || prices === void 0 ? void 0 : prices.discountedPrice) || '' : clbbPrice || '',
        dimension143: purchaseType,
        dimension144: lineItemType,
        dimension145: 'true',
        metric140: !isCLBBSubscription ? (prices === null || prices === void 0 ? void 0 : prices.original) || '' : clbbPrice || '',
        metric141: (prices === null || prices === void 0 ? void 0 : prices.discountAmount) || '',
        metric142: (prices === null || prices === void 0 ? void 0 : prices.vatAmount) || ''
    };
};
const getSunglassesAttributes = (sharedAttributes, product, activeVariant, locale)=>swcHelpers.objectSpread({}, sharedAttributes, {
        category: DataLayer.DataLayerTypes.Category.Sunglasses,
        brand: activeVariant.brand || '',
        variant: getLocalizedAttrValue(activeVariant, 'frameShape', locale).toLowerCase(),
        dimension16: getJoinedLocalizedAttrValues(activeVariant, 'gender', locale).toLowerCase() || '',
        dimension140: product.masterVariant.sku || '',
        dimension146: getLocalizedAttrValue(activeVariant, 'frameMaterial', locale).toLowerCase(),
        dimension147: getJoinedLocalizedAttrValues(activeVariant, 'frameColor', locale).toLowerCase()
    })
;
const getContactLensesAttributes = (sharedAttributes, product, activeVariant, locale)=>{
    const brandValue = getAttrValue(activeVariant, 'brand');
    const brand = brandValue && typeof brandValue === 'string' ? brandValue : '';
    return swcHelpers.objectSpread({}, sharedAttributes, {
        name: activeVariant.name || '',
        brand,
        category: DataLayer.DataLayerTypes.Category.ContactLenses,
        variant: getLocalizedAttrValue(activeVariant, 'lensType', locale),
        dimension140: product.masterVariant.sku || '',
        dimension148: getLocalizedAttrValue(activeVariant, 'replacementFrequency', locale),
        dimension146: getLocalizedAttrValue(activeVariant, 'material', locale).toLowerCase()
    });
};
const getPrescriptionGlassesAttributes = (sharedAttributes, product, activeVariant, locale)=>{
    const frameMaterial = getJoinedLocalizedAttrValues(activeVariant, 'frameMaterial', locale).toLowerCase();
    const frameColor = getJoinedLocalizedAttrValues(activeVariant, 'frameColor', locale).toLowerCase();
    const frameShape = getJoinedLocalizedAttrValues(activeVariant, 'frameShape', locale).toLowerCase();
    const gender = getJoinedLocalizedAttrValues(activeVariant, 'gender', locale).toLowerCase();
    return swcHelpers.objectSpread({}, sharedAttributes, {
        brand: activeVariant.brand || '',
        variant: frameShape || '',
        dimension140: product.masterVariant.sku || '',
        dimension16: gender,
        category: DataLayer.DataLayerTypes.Category.Optical
    }, frameMaterial ? {
        dimension146: frameMaterial
    } : null, frameColor ? {
        dimension147: frameColor
    } : null);
};
const getContactLensesBundleBeneluxAttributes = (sharedAttributes, activeVariant, locale)=>swcHelpers.objectSpread({}, sharedAttributes, {
        category: DataLayer.DataLayerTypes.Category.ContactLenses,
        variant: getLocalizedAttrValue(activeVariant, 'lensType', locale)
    })
;
const getSolutionsAttributes = (sharedAttributes, product, activeVariant)=>swcHelpers.objectSpread({}, sharedAttributes, {
        name: activeVariant.name || '',
        category: DataLayer.DataLayerTypes.Category.Solutions,
        brand: activeVariant.brand || '',
        variant: String(getAttrValue(activeVariant, 'packageOption')),
        dimension140: String(product.masterVariant.sku),
        dimension146: String(getAttrValue(activeVariant, 'solutionType'))
    })
;
const getReadyReadersAttributes = (sharedAttributes, product, activeVariant, locale)=>swcHelpers.objectSpread({}, sharedAttributes, {
        category: DataLayer.DataLayerTypes.Category.ReadyReaders,
        brand: activeVariant.brand || '',
        variant: getLocalizedAttrValue(activeVariant, 'frameShape', locale).toLowerCase(),
        dimension16: getJoinedLocalizedAttrValues(activeVariant, 'gender', locale).toLowerCase() || '',
        dimension140: product.masterVariant.sku || '',
        dimension146: getLocalizedAttrValue(activeVariant, 'frameMaterial', locale).toLowerCase(),
        dimension147: getJoinedLocalizedAttrValues(activeVariant, 'frameColor', locale).toLowerCase()
    })
;
const getAccessoriesAttributes = (sharedAttributes, product, activeVariant, locale)=>swcHelpers.objectSpread({}, sharedAttributes, {
        category: DataLayer.DataLayerTypes.Category.Accessories,
        brand: activeVariant.brand || '',
        variant: getLocalizedAttrValue(activeVariant, 'accessoryType', locale).toLowerCase(),
        dimension16: getJoinedLocalizedAttrValues(activeVariant, 'gender', locale).toLowerCase(),
        dimension146: getLocalizedAttrValue(activeVariant, 'material', locale).toLowerCase(),
        dimension147: getJoinedLocalizedAttrValues(activeVariant, 'accessoryColor', locale).toLowerCase(),
        dimension140: product.masterVariant.sku || ''
    })
;
const mapProductToDataLayerProductObject = (purchaseType, product, activeSku, locale = Locales['en-GB'], productType, pricePerMonth, offering)=>{
    var ref, ref1, ref2, ref3, ref4, ref5, ref6;
    const activeVariant = [
        product.masterVariant,
        ...product.variants
    ].find((variant)=>variant.sku === activeSku
    ) || product.masterVariant;
    const sharedAttributes = getSharedAttributes(product, purchaseType, activeVariant, productType || DataLayer.DataLayerTypes.LineItemType.Single, pricePerMonth, offering);
    if (((ref = product.productType.obj) === null || ref === void 0 ? void 0 : ref.name) === 'Sunglasses') {
        return getSunglassesAttributes(sharedAttributes, product, activeVariant, locale);
    }
    if (((ref1 = product.productType.obj) === null || ref1 === void 0 ? void 0 : ref1.name) === 'Contact-lenses') {
        return getContactLensesAttributes(sharedAttributes, product, activeVariant, locale);
    }
    if (((ref2 = product.productType.obj) === null || ref2 === void 0 ? void 0 : ref2.name) === 'Contact-lens-bundles') {
        return getContactLensesBundleBeneluxAttributes(sharedAttributes, activeVariant, locale);
    }
    if (((ref3 = product.productType.obj) === null || ref3 === void 0 ? void 0 : ref3.name) === 'Frames') {
        return getPrescriptionGlassesAttributes(sharedAttributes, product, activeVariant, locale);
    }
    if (((ref4 = product.productType.obj) === null || ref4 === void 0 ? void 0 : ref4.name) === 'Ready-readers') {
        return getReadyReadersAttributes(sharedAttributes, product, activeVariant, locale);
    }
    if (((ref5 = product.productType.obj) === null || ref5 === void 0 ? void 0 : ref5.name) === 'Accessories') {
        return getAccessoriesAttributes(sharedAttributes, product, activeVariant, locale);
    }
    if (((ref6 = product.productType.obj) === null || ref6 === void 0 ? void 0 : ref6.name) === 'Solutions') {
        return getSolutionsAttributes(sharedAttributes, product, activeVariant);
    }
};

export { mapProductToDataLayerProductObject };
//# sourceMappingURL=ProductMapper.js.map
